import React, { useState, useEffect, useMemo } from 'react';
import { X, Moon, Loader2, CheckCircle } from 'lucide-react';
import { setDriverPhoto } from './DriverPhoto';

/* ------------------------------------------------------------------ *
 * Hire an applicant into the NIGHT SORT team.
 *
 * The Applicants register feeds two different registers: contractor drivers
 * (Drivers sheet, via AddDriverPanel) and PAYG night sorters (the Supabase
 * `sorters` table, via sorter-registry-sb addSorter). This panel is the second
 * road — it carries the details already collected at interview / onboarding
 * across so the office doesn't retype them, then creates the sorter and hands
 * back the portal PIN.
 *
 * Deliberately NOT carried over: the TFN, even when the applicant supplied one —
 * tax file numbers live only in the encrypted vault, entered by the sorter in their
 * own portal. Super fund + member no. ARE carried (payroll needs them), but that is
 * reference data only: their signed super declaration is still outstanding.
 * ------------------------------------------------------------------ */

const REGISTRY_URL = '/.netlify/functions/sorter-registry-sb';
const post = (body) => fetch(REGISTRY_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());

// Mirrors SortersAdminPage — pay level follows grade (see levelFor in sorter-pay-sb.js).
const GRADES = [
  { id: 'grade1', label: 'Grade 1 — Sorter (L1)' },
  { id: 'grade2', label: 'Grade 2 (L2)' },
  { id: 'grade3', label: 'Grade 3 (L1)' },
  { id: 'grade6', label: 'Grade 6 — Team Leader (L3)' },
  { id: 'grade8', label: 'Grade 8 — Foreperson (L1)' },
];
const FACILITIES = ['', 'City', 'Darlinghurst', 'Broadway'];
const EMP_OPTIONS = ['Casual', 'Part Time', 'Full Time'];

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { width: '100%', background: 'var(--hp-surface)', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '9px 11px', fontSize: '.9rem', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };

// ISO (YYYY-MM-DD, what the applicants table stores) → the DD/MM/YYYY the sorter
// registry is written in everywhere else. Anything else is passed through as typed.
const toAuDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((v || '').toString());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '');
};

// Best-effort: copy the applicant's head-shot to the shared driver-photo store under
// the new sorter login, so their face shows on Night Shift / the roster. Same-origin
// (applicant-doc), so the canvas isn't tainted. Never blocks the hire.
async function copyPhoto(applicant, login) {
  if (!applicant.photoUrl) return false;
  const dataUrl = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, 320 / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(img.width * scale));
        c.height = Math.max(1, Math.round(img.height * scale));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', 0.82));
      } catch { resolve(''); }
    };
    img.onerror = () => resolve('');
    img.src = applicant.photoUrl;
  });
  if (!dataUrl) return false;
  await setDriverPhoto(login, dataUrl);
  return true;
}

export default function HireSorterPanel({ applicant, onClose, onSuccess }) {
  // Two places an applicant's details can already be on file: the self-service
  // onboarding form (driver road) and a returned Request-for-Information (the SMS
  // form — what most night-sort candidates come back through). The info request is
  // the later, more explicit answer, so it wins where both hold a field.
  const ob = applicant.onboarding || {};
  const ir = (applicant.infoRequest && applicant.infoRequest.data) || {};
  const [taken, setTaken] = useState(() => new Set());
  const [loadingLogins, setLoadingLogins] = useState(true);
  const [form, setForm] = useState(() => ({
    login: '',
    name: applicant.fullName || '',
    mobile: ir.mobile || applicant.mobile || '',
    email: ir.email || applicant.email || '',
    dob: toAuDate(ir.dateOfBirth || applicant.dateOfBirth),
    address: ir.currentAddress || (ob.sameAddress === false ? (ob.currentAddress || '') : ''),
    facility: '',
    employmentType: 'Casual',
    grade: 'grade1',
    baseRate: '',
    startDate: '',
    pin: '',
    emergencyName: ir.emergencyName || ob.emergencyName || '',
    emergencyPhone: ir.emergencyPhone || ob.emergencyPhone || '',
    superFund: ir.superFund || '',
    superMemberNo: ir.superMember || '',
    accountName: ir.accountName || ob.accountName || '',
    bsb: ir.bsb || ob.bsb || '',
    accNumber: ir.accNumber || ob.accNumber || '',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);   // { login, pin }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Existing logins, so a clash is caught before saving. The login itself is typed by
  // the office (it has to match what AP issues) — the engine never makes one up.
  useEffect(() => {
    let alive = true;
    post({ action: 'listSorters' })
      .then((d) => {
        if (!alive) return;
        const s = new Set((d.sorters || []).map(x => (x.Login || '').toUpperCase()).filter(Boolean));
        setTaken(s);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingLogins(false); });
    return () => { alive = false; };
  }, [applicant.fullName]);

  const login = form.login.trim().toUpperCase();
  const loginValid = /^[A-Z0-9]{3,20}$/.test(login);
  const clash = loginValid && taken.has(login);
  const carried = useMemo(() => [
    form.dob && 'date of birth',
    form.address && 'address',
    form.emergencyName && 'emergency contact',
    form.accountName && 'bank details',
    form.superFund && 'super fund',
  ].filter(Boolean), [form.dob, form.address, form.emergencyName, form.accountName, form.superFund]);

  async function save() {
    if (!loginValid || clash) return;
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setErr(''); setSaving(true);
    try {
      const res = await post({
        action: 'addSorter',
        sorter: {
          ...form, login,
          classification: form.grade === 'grade6' ? 'Team Leader' : 'Sorter',
          permanentNight: true,
        },
      });
      if (!res.success) throw new Error(res.error || 'Could not add the sorter.');
      setDone({ login: res.login || login, pin: res.pin || '' });
      // Face on the night-shift roster — nice to have, never fatal.
      try { await copyPhoto(applicant, res.login || login); } catch {}
      await onSuccess(res.login || login, res.pin || '');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const field = (label, k, opts, extra) => (
    <div>
      <label style={labelStyle}>{label}</label>
      {opts
        ? <select value={form[k]} onChange={set(k)} style={inputStyle}>{opts.map(o => (typeof o === 'string' ? <option key={o} value={o}>{o || '—'}</option> : <option key={o.id} value={o.id}>{o.label}</option>))}</select>
        : <input value={form[k]} onChange={set(k)} style={inputStyle} {...(extra || {})} />}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 4000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 520, maxWidth: '96%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Moon size={12} /> Night Sort
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Add {applicant.fullName || 'applicant'} to the sorters</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {done ? (
          <div style={{ padding: '14px 16px', background: 'rgba(22,163,74,.12)', border: '1px solid rgba(22,163,74,.4)', borderRadius: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-green)', fontWeight: 700, marginBottom: 8 }}>
              <CheckCircle size={16} /> {done.login} added to the night sorters
            </div>
            {done.pin && (
              <div style={{ fontSize: '.85rem', color: 'var(--hp-text)' }}>
                Portal PIN <strong style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem', letterSpacing: '.1em' }}>{done.pin}</strong> — give this to them to sign in at the sorter portal.
              </div>
            )}
            <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 10 }}>
              They still need to supply their TFN and super declaration themselves — those are entered in the sorter portal, never here.
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 14 }} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 16, lineHeight: 1.5 }}>
              Creates a PAYG night-sort record in the sorters registry (separate from contractor Drivers) and marks this applicant hired.
              {carried.length > 0 && <> Carried over from their file: {carried.join(', ')}.</>}
            </div>

            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Login *</label>
                <input value={form.login} onChange={e => setForm(f => ({ ...f, login: e.target.value.toUpperCase() }))} style={inputStyle} placeholder={loadingLogins ? 'loading…' : 'e.g. NOLANT'} />
                <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 3 }}>Uppercase — this is their sign-in and the key across every night-sort table (it can't be renamed later).</div>
              </div>
              {field('Full name *', 'name')}
              {field('Mobile', 'mobile')}
              {field('Email', 'email')}
              {field('Date of birth', 'dob', null, { placeholder: 'DD/MM/YYYY' })}
              {field('Facility', 'facility', FACILITIES)}
              {field('Employment', 'employmentType', EMP_OPTIONS)}
              {field('Classification', 'grade', GRADES)}
              {field('Base rate', 'baseRate', null, { placeholder: 'blank = award rate' })}
              {field('Start date', 'startDate', null, { placeholder: 'blank = today' })}
              <div>
                <label style={labelStyle}>PIN</label>
                <input value={form.pin} onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={inputStyle} placeholder="blank = auto-generate" />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Address</label>
              <input value={form.address} onChange={set('address')} style={inputStyle} />
            </div>

            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
              {field('Emergency contact', 'emergencyName')}
              {field('Emergency phone', 'emergencyPhone')}
            </div>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hp-border)' }}>
              <div style={{ ...labelStyle, marginBottom: 10 }}>Bank (for PAYG pay)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                {field('Account name', 'accountName')}
                <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12 }}>
                  {field('BSB', 'bsb')}
                  {field('Account number', 'accNumber')}
                </div>
                <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {field('Super fund', 'superFund')}
                  {field('Super member no.', 'superMemberNo')}
                </div>
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                Bank + super are hidden from the sorter list once saved — they reveal one at a time behind the vault secret. TFN is never stored here.
              </div>
            </div>

            {form.login && !loginValid && <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginTop: 12 }}>Login must be 3–20 characters, letters and numbers only.</div>}
            {clash && <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginTop: 12 }}>{login} is already a sorter — pick a different login.</div>}
            {err && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 8, padding: '9px 12px', marginTop: 14, fontSize: '.8rem' }}>{err}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={!loginValid || clash || saving || !form.name.trim()}>
                {saving ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Adding…</> : <><Moon size={14} /> Add to night sorters</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
